import { useState } from 'react';
import { Icon, Pill, StateBox } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import { inCoverage } from '../data/shape.js';
import type { InspectorApi, InspectorProfile, InspectorTask } from '../data/types.js';
import { CardLabel, PhoneButton } from '../components/PhoneChrome.js';

type Decision = 'accepted' | 'declined';

/** Tasks — accept or decline routed assignments, gated to the inspector's coverage (PRD §10.2, §14.3). */
export function Tasks({
  api,
  profile,
  onOpen,
}: {
  api: InspectorApi;
  profile: InspectorProfile;
  onOpen: (task: InspectorTask) => void;
}) {
  const { data, loading, error } = useAsync(() => api.getTasks());
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  if (loading) return <StateBox kind="loading" title="Loading your tasks" />;
  if (error || !data) return <StateBox kind="error" title="Could not load tasks">{error}</StateBox>;

  const decide = (id: string, d: Decision): void => setDecisions((prev) => ({ ...prev, [id]: d }));

  const visible = data.filter((t) => decisions[t.id] !== 'declined');
  if (visible.length === 0) {
    return (
      <div style={{ padding: 14 }}>
        <StateBox kind="empty" title="No open assignments">
          Routed tasks within your coverage will appear here.
        </StateBox>
      </div>
    );
  }

  return (
    <div style={{ padding: 14, display: 'grid', gap: 12 }}>
      {visible.map((t) => {
        const decision = decisions[t.id];
        const status: InspectorTask['status'] = decision ?? t.status;
        const covered = inCoverage(t, profile);
        const accepted = status === 'accepted';
        const dueToday = t.due.includes('today');
        return (
          <div key={t.id} style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <CardLabel>{t.id} · {t.method_label}</CardLabel>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {!covered && <Pill tone="f">outside coverage</Pill>}
                <Pill tone={accepted ? 'm' : 'n'}>{status}</Pill>
              </span>
            </div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{t.asset}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>
              {t.supplier} · {t.place}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              <Icon name="shield" size={13} /> resolves {t.resolves_check_label}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 38, marginTop: 4, gap: 8 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: dueToday ? 'var(--flag-ink)' : 'var(--muted)' }}>
                due {t.due}
              </span>
              {accepted ? (
                <PhoneButton primary onClick={() => onOpen(t)}>
                  Open capture
                </PhoneButton>
              ) : (
                <span style={{ display: 'flex', gap: 6 }}>
                  <PhoneButton onClick={() => decide(t.id, 'declined')}>Decline</PhoneButton>
                  <PhoneButton primary disabled={!covered} onClick={() => decide(t.id, 'accepted')}>
                    Accept
                  </PhoneButton>
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
